import { useState, useEffect, useCallback } from 'react';
import { fetchInquiries, deleteInquiry, type Inquiry } from '@/lib/api';

export default function AdminHistory() {
  const [items, setItems] = useState<Inquiry[]>([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState<Inquiry | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<Inquiry | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchInquiries();
      const completedOnly = data.filter((i) => i.status === 'completed');
      setItems(completedOnly);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const filtered = items.filter((item) =>
    (item.name || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.email || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.company || '').toLowerCase().includes(search.toLowerCase()) ||
    (item.message || '').toLowerCase().includes(search.toLowerCase())
  );

  const handlePermanentDelete = async () => {
    if (!confirmDelete) return;
    setError('');
    try {
      const success = await deleteInquiry(confirmDelete.id);
      if (!success) throw new Error('Delete failed');
      setConfirmDelete(null);
      setSelected(null);
      loadHistory();
    } catch (err) {
      setError('Failed to delete. Please try again.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Inquiry History</h1>
          <p className="text-sm text-white/50 mt-1">Completed inquiries. Permanently delete to clear records.</p>
        </div>
        <span className="text-xs text-white/40">{filtered.length} completed</span>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm flex items-center justify-between">
          <span>{error}</span>
          <button onClick={loadHistory} className="text-red-300 hover:text-red-200 font-medium">
            Retry
          </button>
        </div>
      )}

      <div className="relative">
        <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-white/40 text-sm" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search history..."
          className="w-full pl-9 pr-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white text-sm focus:border-neon-500 outline-none transition"
        />
      </div>

      <div className="glass rounded-xl border border-white/5 overflow-hidden">
        {loading ? (
          <div className="py-20 text-center text-white/50 text-sm">
            <i className="ri-loader-4-line animate-spin mr-2" />
            Loading history...
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-neon-500/10 text-neon-400 text-2xl mb-4">
              <i className="ri-history-line" />
            </div>
            <h3 className="font-display font-semibold text-white">No completed inquiries</h3>
            <p className="mt-2 text-sm text-white/50">Inquiries marked as complete will appear here.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/5 text-white/50 text-xs uppercase tracking-wider">
                  <th className="px-4 py-3 text-left">Name</th>
                  <th className="px-4 py-3 text-left">Service</th>
                  <th className="px-4 py-3 text-left">Budget</th>
                  <th className="px-4 py-3 text-left">Completed</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((item) => (
                  <tr key={item.id} className="border-b border-white/5 hover:bg-white/[0.02] transition">
                    <td className="px-4 py-4">
                      <div className="font-medium text-white">{item.name}</div>
                      <div className="text-xs text-white/40">{item.email || '—'}</div>
                    </td>
                    <td className="px-4 py-4 text-white/80">{item.service || '—'}</td>
                    <td className="px-4 py-4 text-white/70 font-mono">{item.budget || '—'}</td>
                    <td className="px-4 py-4 text-white/50 font-mono text-xs">
                      {new Date(item.created_at).toLocaleString()}
                    </td>
                    <td className="px-4 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => setSelected(item)}
                          className="text-xs text-neon-400 hover:text-neon-300 font-medium transition"
                        >
                          View
                        </button>
                        <button
                          onClick={() => setConfirmDelete(item)}
                          className="w-7 h-7 flex items-center justify-center rounded-lg text-red-400 hover:bg-red-500/10 transition"
                          title="Permanently Delete"
                        >
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Detail Modal */}
      {selected && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="font-display font-semibold text-lg text-white">Inquiry Details</h2>
              <button onClick={() => setSelected(null)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-white/5 text-white/50 hover:text-white transition">
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-neon-600/30 to-neon-400/30 flex items-center justify-center text-neon-400 font-bold text-sm">
                  {(selected.name || '?').charAt(0)}
                </div>
                <div>
                  <div className="font-medium text-white">{selected.name}</div>
                  <div className="text-xs text-white/50">{selected.email || '—'}</div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-white/5">
                <div className="text-xs text-white/40 mb-1">Service</div>
                <div className="text-sm text-white/80">{selected.service || '—'}</div>
              </div>
              <div className="p-3 rounded-lg bg-white/5">
                <div className="text-xs text-white/40 mb-1">Message</div>
                <div className="text-sm text-white/80 leading-relaxed">{selected.message || '—'}</div>
              </div>
              <button
                onClick={() => setSelected(null)}
                className="w-full px-4 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permanent Delete Confirm */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
          <div className="glass rounded-2xl border border-white/10 max-w-sm w-full p-6">
            <div className="w-12 h-12 mx-auto flex items-center justify-center rounded-full bg-red-500/15 text-red-400 text-2xl mb-4">
              <i className="ri-alert-line" />
            </div>
            <h3 className="font-display font-semibold text-white text-center">Permanently Delete?</h3>
            <p className="mt-2 text-sm text-white/50 text-center">
              This will permanently remove the inquiry for <span className="text-white font-medium">{confirmDelete.name}</span>. This action cannot be undone.
            </p>
            <div className="flex gap-3 mt-6">
              <button
                onClick={handlePermanentDelete}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition whitespace-nowrap"
              >
                Delete
              </button>
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 rounded-md text-sm font-semibold border border-white/10 text-white/70 hover:bg-white/5 transition whitespace-nowrap"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}